import { type FormEvent, type ReactNode } from 'react'
import { useForm, Head } from '@inertiajs/react'
import Logo from '@/components/Logo'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

type Props = {
  token: string
  member: {
    email: string
    first_name: string
    last_name: string
    title: string
  }
  organization: {
    name: string
  }
}

export default function InvitationShow({ token, member, organization }: Props) {
  const { data, setData, patch, processing, errors } = useForm({
    first_name: member.first_name,
    last_name: member.last_name,
    title: member.title,
    password: '',
    password_confirmation: '',
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    patch(`/organizations/invitations/${token}`)
  }

  return (
    <>
      <Head title={`Join ${organization.name}`} />
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background via-accent/10 to-primary/5 px-4">
        <div className="w-full max-w-md">
          <div className="mb-8 flex justify-center">
            <Logo />
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-3xl font-semibold">Join {organization.name}</CardTitle>
              <CardDescription>
                Set your password to accept this invitation. You'll sign in as <strong>{member.email}</strong>.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First name</Label>
                    <Input
                      id="first_name"
                      value={data.first_name}
                      onChange={e => setData('first_name', e.target.value)}
                      autoFocus
                    />
                    {errors.first_name && <p className="text-xs text-destructive">{errors.first_name}</p>}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last name</Label>
                    <Input
                      id="last_name"
                      value={data.last_name}
                      onChange={e => setData('last_name', e.target.value)}
                    />
                    {errors.last_name && <p className="text-xs text-destructive">{errors.last_name}</p>}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="title">Your role at {organization.name}</Label>
                  <Input
                    id="title"
                    value={data.title}
                    onChange={e => setData('title', e.target.value)}
                    placeholder="e.g. Event Coordinator"
                  />
                  {errors.title && <p className="text-xs text-destructive">{errors.title}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={data.password}
                    onChange={e => setData('password', e.target.value)}
                    autoComplete="new-password"
                  />
                  {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password_confirmation">Confirm password</Label>
                  <Input
                    id="password_confirmation"
                    type="password"
                    value={data.password_confirmation}
                    onChange={e => setData('password_confirmation', e.target.value)}
                    autoComplete="new-password"
                  />
                  {errors.password_confirmation && (
                    <p className="text-xs text-destructive">{errors.password_confirmation}</p>
                  )}
                </div>

                <Button type="submit" className="w-full" disabled={processing}>
                  Accept invitation
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

InvitationShow.layout = (page: ReactNode) => page
